import { Link, useLocation } from 'react-router-dom';
import { useInboundMailbox } from '@/api/hooks/useInboundMailbox';
import { scopeHref } from '@/app/shell/scope';
import {
  gmailAgentPrompt,
  gmailCreateFilterUrl,
  gmailFromEmails,
  gmailTakeoutUrl,
  RECEIPT_FILTER_GMAIL_HELP_URL,
  RECEIPT_FILTER_GMAIL_LABEL,
  RECEIPT_FILTER_GMAIL_PASTE,
  RECEIPT_FILTER_GMAIL_TAKEOUT_HELP_URL,
  RECEIPT_FILTER_ICLOUD_HELP_URL,
  RECEIPT_FILTER_ICLOUD_MAIL_URL,
  RECEIPT_FILTER_OUTLOOK_HELP_URL,
  RECEIPT_FILTER_OUTLOOK_RULES_URL,
  RECEIPT_FILTER_SUBJECT_TOKENS,
} from './receiptFilter';
import { ConfirmationRows, CopyField, GmailDeepLinks, HostLink } from './receiptForwardingControls';
import { TakeoutUploadSection } from './TakeoutUploadSection';

export function ReceiptForwardingInstructionsPage() {
  const { search } = useLocation();
  const mailboxQuery = useInboundMailbox();
  const address = mailboxQuery.data?.status === 'active'
    ? mailboxQuery.data.address?.toLowerCase() ?? null
    : null;
  const verificationCode = mailboxQuery.data?.verification_code ?? null;
  const verificationUrl = mailboxQuery.data?.verification_url ?? null;
  const hasConfirmation = Boolean(
    mailboxQuery.data
    && ((mailboxQuery.data.verifications ?? []).length > 0
      || mailboxQuery.data.verification_code
      || mailboxQuery.data.verification_url),
  );
  const gmailEmails = mailboxQuery.data?.gmail_addresses?.length
    ? mailboxQuery.data.gmail_addresses
    : gmailFromEmails(mailboxQuery.data?.verifications);

  return (
    <section className="mx-auto w-full max-w-2xl space-y-6 p-4 sm:p-6" aria-labelledby="receipt-setup-heading">
      <div>
        <Link to={scopeHref('/settings', search)} className="ms-tap-44 inline-flex min-h-12 items-center text-sm text-link">
          ‹ Settings
        </Link>
        <h1 id="receipt-setup-heading" tabIndex={-1} className="text-2xl font-bold">
          Set up forwarding
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Forward order confirmations, shipments, and receipts. Keep your own copy.
          Bank transactions stay the money record. Set the rule on Gmail, iCloud Mail,
          or Outlook — not in the iPhone Mail app.
        </p>
      </div>

      {mailboxQuery.isPending && (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading receipt email…</p>
      )}
      {mailboxQuery.isError && (
        <p role="alert" className="text-sm">Receipt email could not be loaded. You can still set the filter, then copy the address from Settings.</p>
      )}
      {address && (
        <CopyField
          id="receipt-setup-address"
          label="Forwarding address"
          value={address}
          testId="receipt-setup-address"
        />
      )}

      <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-filter-heading">
        <h2 id="receipt-filter-heading" className="text-lg font-semibold">Filter recipe</h2>
        <p className="text-sm text-muted-foreground">
          Gmail: paste this in Subject. Leave From and Has the words empty. Do not put a date in the saved filter — Gmail will not match new mail. Do not forward all mail.
          Do not delete on forward.
        </p>
        <CopyField
          id="receipt-filter-paste"
          label="Gmail Subject"
          value={RECEIPT_FILTER_GMAIL_PASTE}
          testId="receipt-filter-paste"
        />
        <p className="text-sm text-muted-foreground">
          iCloud and Outlook cannot take that search. Add one subject rule per subject token:
          {' '}{RECEIPT_FILTER_SUBJECT_TOKENS.map((token) => (
            token.includes(' ') || token.includes('-') ? `"${token}"` : token
          )).join(', ')}.
        </p>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-gmail-heading">
        <h2 id="receipt-gmail-heading" className="text-lg font-semibold">Gmail</h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>Settings (gear) → See all settings → Forwarding and POP/IMAP → Add a forwarding address.</li>
          <li>
            {address
              ? <>Paste <span className="font-mono">{address}</span> → Next → Proceed.</>
              : 'Paste the forwarding address from this page → Next → Proceed.'}
          </li>
          <li>
            Return to this page. When the confirmation appears, {verificationCode ? 'copy the code below' : 'copy the code'}
            {verificationUrl || hasConfirmation ? ' or open the confirmation link' : ''}, then finish in Gmail.
            {hasConfirmation && mailboxQuery.data && (
              <div className="mt-2">
                <ConfirmationRows mailbox={mailboxQuery.data} />
              </div>
            )}
          </li>
          <li>Leave Forwarding as the default (Disable forwarding). Do not choose “Forward a copy of incoming mail to” — that sends every message. Only the filter below should forward.</li>
          <li>
            Open Gmail create-filter. Subject should already have the search below. Leave From and Has the words empty. If Subject is empty, copy the search below and paste it there.
            <div className="mt-2 flex flex-col items-start gap-3">
              <GmailDeepLinks
                label="Open Gmail create-filter"
                hrefFor={gmailCreateFilterUrl}
                emails={gmailEmails}
              />
              <CopyField
                id="receipt-gmail-subject-search"
                label="Subject"
                value={RECEIPT_FILTER_GMAIL_PASTE}
                testId="receipt-gmail-has-the-words"
              />
            </div>
          </li>
          <li>
            Create filter → Forward it to the verified address. Apply the label {RECEIPT_FILTER_GMAIL_LABEL}
            {' '}(create it if needed). Check Also apply filter to matching conversations. Do not check Delete it.
            Keep Gmail’s copy.
          </li>
          <li>
            Optional: paste this to a Gmail-connected agent. It should add the forwarding address, create the subject filter and the {RECEIPT_FILTER_GMAIL_LABEL} label, then Forward a small set of matching threads or request Takeout of that label. You still confirm in Settings — Gmail sends that mail to the receipt address, not your inbox.
            <div className="mt-2">
              <CopyField
                id="receipt-gmail-agent-prompt"
                label="Agent prompt"
                value={gmailAgentPrompt({ address })}
                testId="receipt-gmail-agent-prompt"
                multiline
              />
            </div>
          </li>
          <li>Open the {RECEIPT_FILTER_GMAIL_LABEL} label in Gmail and confirm it has conversations.</li>
          <li>
            The saved filter does not forward old mail. If you have only a few matching threads, open each one → Forward → paste the receipt address → Send. Keep Gmail’s copy. That sends those receipts to MoneySkyline. Skip Takeout.
          </li>
          <li>
            Optional — only if you have many matching threads and want a zip to keep. Takeout does not send mail to MoneySkyline. Open Google Takeout signed into that same Gmail only. Check the avatar. Do not use another Google account.
            <div className="mt-2">
              <GmailDeepLinks
                label="Open Google Takeout"
                hrefFor={gmailTakeoutUrl}
                emails={gmailEmails}
              />
            </div>
          </li>
          <li>
            Deselect all products. Tick Mail only. Leave Drive, Photos, and every other product off.
            If All data included is missing, untick Mail and tick it again.
          </li>
          <li>On the Mail row, click All data included (or All Mail data included). Do not click Multiple formats.</li>
          <li>
            Uncheck Include all messages in Mail. Tick {RECEIPT_FILTER_GMAIL_LABEL} only. OK.
            The export must be Mail and the {RECEIPT_FILTER_GMAIL_LABEL} label only.
          </li>
          <li>
            Next step → Frequency: Export once. File type: .zip. File size: 2GB.
            Transfer: Send download link via email. Create export. When Google emails you, open the link and download the zip.
            Do not forward that email to the receipt address.
          </li>
        </ol>
        <div className="flex flex-col items-start gap-1">
          <HostLink href={RECEIPT_FILTER_GMAIL_HELP_URL}>Gmail forwarding help</HostLink>
          <HostLink href={RECEIPT_FILTER_GMAIL_TAKEOUT_HELP_URL}>Google Takeout help</HostLink>
        </div>
      </section>

      <TakeoutUploadSection />

      <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-icloud-heading">
        <h2 id="receipt-icloud-heading" className="text-lg font-semibold">iCloud Mail</h2>
        <p className="text-sm text-muted-foreground">
          Set this on icloud.com, not in iOS Mail. One condition per rule.
        </p>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>Open icloud.com/mail and sign in.</li>
          <li>Settings → Rules → add a rule named Receipts.</li>
          <li>Subject contains one token from the recipe. Action: Forward to the MoneySkyline address.</li>
          <li>Add. Repeat until all eight subject tokens have a rule.</li>
          <li>Do not use Mail Forwarding → “Forward my email to”. Do not delete after forwarding. Wait up to 15 minutes. Apple does not send a confirmation code.</li>
          <li>iCloud rules do not send old mail. If you have only a few old receipts, open each one and Forward to the MoneySkyline address. Keep your copy.</li>
        </ol>
        <div className="flex flex-col items-start gap-1">
          <HostLink href={RECEIPT_FILTER_ICLOUD_MAIL_URL}>Open iCloud Mail</HostLink>
          <HostLink href={RECEIPT_FILTER_ICLOUD_HELP_URL}>iCloud rules help</HostLink>
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-outlook-heading">
        <h2 id="receipt-outlook-heading" className="text-lg font-semibold">Outlook</h2>
        <p className="text-sm text-muted-foreground">
          Use an inbox rule. Mail → Forwarding is all mail.
        </p>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>Settings → Mail → Rules → Add new rule. Name it Receipts.</li>
          <li>Subject includes the recipe tokens (one OR list, or one rule per token).</li>
          <li>Action: Forward to the MoneySkyline address. Do not add Delete. Keep a copy.</li>
          <li>Work or school accounts may block external forward until an admin allows it. First stored matching mail is the proof — Outlook has no Gmail-style confirm code.</li>
          <li>Inbox rules do not send old mail. If you have only a few old receipts, open each one and Forward to the MoneySkyline address. Keep your copy.</li>
        </ol>
        <div className="flex flex-col items-start gap-1">
          <HostLink href={RECEIPT_FILTER_OUTLOOK_RULES_URL}>Open Outlook rules</HostLink>
          <HostLink href={RECEIPT_FILTER_OUTLOOK_HELP_URL}>Outlook rules help</HostLink>
        </div>
      </section>
    </section>
  );
}
